import React, { useState, useRef } from 'react';
import { Input, Button, message, Col } from 'antd';
import type { InputRef } from 'antd';
import './OtpForm.less';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../store/store';
import { useMembershipAuth } from '../../../hooks/useMembershipAuth';
import {
  setIsOtpAuthCheckModalOpen,
  setMembershipUser,
} from '../../../store/config';
import SpinLoader from '@skygroup/shared/components/spin-loader/SpinLoader';
import ModelApiMessage, {
  ApiStatus,
  ApiStatusMessage,
} from '@skygroup/shared/components/crud-helpers/ModelApiMessage';

interface OTPFormProps {
  setIsLoginSuccess: (success: boolean) => void;
  sessionId: string;
}

const columnSize = { xs: 20, md: 35 };

const OTPForm: React.FC<OTPFormProps> = ({ setIsLoginSuccess, sessionId }) => {
  const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { auth: t } = useSelector(
    (state: RootState) => state.config.translations
  );
  const { verifyOTPFn } = useMembershipAuth();
  const [apiStatusMessage, setApiStatusMessage] = useState(
    {} as ApiStatusMessage
  );
  const inputRefs = useRef<Array<InputRef | null>>([]);

  const handleChange = (value: string, index: number) => {
    if (/^[0-9]$/.test(value) || value === '') {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < otp.length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === 'Backspace' && otp[index] === '' && index > 0) {
      const prevIndex = index - 1;
      setOtp((prev) => {
        const newOtp = [...prev];
        newOtp[prevIndex] = '';
        return newOtp;
      });
      setTimeout(() => {
        inputRefs.current[prevIndex]?.focus();
      }, 0);
    }
  };

  const handlePaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
    index: number
  ) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setOtp(digits);
      inputRefs.current[otp.length - 1]?.focus();
    } else {
      const digit = pastedData.charAt(0);
      if (/^\d$/.test(digit)) {
        handleChange(digit, index);
      }
    }
  };

  const handleSubmit = async () => {
    const otpString = otp.join('');
    if (otpString.length === otp.length) {
      setLoading(true);
      try {
        const membershipUserWithExpiration = await verifyOTPFn(
          sessionId,
          otpString
        );
        const { data, user_name } = membershipUserWithExpiration;
        localStorage.setItem(
          'membershipUser',
          JSON.stringify(membershipUserWithExpiration)
        );
        dispatch(setMembershipUser({ data, user_name }));
        dispatch(setIsOtpAuthCheckModalOpen(false));
        message.success(`${user_name} ${t.loginSuccess}`);
      } catch {
        setApiStatusMessage({
          status: ApiStatus.error,
          data: { message: t.otpError },
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleStartOver = () => {
    setIsLoginSuccess(false);
    setOtp(['', '', '', '', '', '']);
    inputRefs.current[0]?.focus();
  };

  return (
    <SpinLoader isLoading={loading} size="large" onTop={true}>
      <div className="otp-form">
        <div className="otp-inputs">
          {otp.map((digit, index) => (
            <Input
              key={index}
              value={digit}
              maxLength={1}
              className="otp-input"
              onChange={(e) => handleChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={(e) => handlePaste(e, index)}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
            />
          ))}
        </div>
        <Button
          type="primary"
          onClick={handleSubmit}
          block
          style={{ marginTop: 16 }}
          className="btn-style-5"
        >
          {t.verify}
        </Button>
        <Col {...columnSize} offset={1}>
          <ModelApiMessage apiStatusMessage={apiStatusMessage} t={t.crud} />
        </Col>
        <div className="otp-start-over">
          <span onClick={handleStartOver}>{t.otpStartOver}</span>
        </div>
      </div>
    </SpinLoader>
  );
};

export default OTPForm;
